import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Form, Button, Table } from 'react-bootstrap';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const LabTest = ({ appointment, onClose }) => {
  const [type, setType] = useState('single');
  const [labTests, setLabTests] = useState([]);
  const [combos, setCombos] = useState([]);
  const [selectedTest, setSelectedTest] = useState('');
  const [urgency, setUrgency] = useState('Normal');
  const [preferredLab, setPreferredLab] = useState('');
  const [labs, setLabs] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Lab-side state
  const [labRequests, setLabRequests] = useState([]);
  const [reportFiles, setReportFiles] = useState({});

  const showWarning = (message) => {
    toast(message, {
      icon: '⚠️',
      style: {
        background: '#fffbe6',
        color: '#664d03',
        border: '1px solid #ffecb5',
      },
    });
  };

  useEffect(() => {
    const hospitalId = localStorage.getItem('hospitalId');
    if (!hospitalId) return;
    axios
      .get(`${BASE_URL}/api/lab/labs`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        params: { hospital_id: hospitalId },
      })
      .then((res) => setLabs(res.data || []))
      .catch(() => setLabs([]));
  }, []);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user && user.role === 'Lab') {
      axios
        .get(`${BASE_URL}/api/lab/requests`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        })
        .then((res) => setLabRequests(res.data || []))
        .catch(() => setLabRequests([]));
    }
  }, []);

  useEffect(() => {
    const hospitalId = localStorage.getItem('hospitalId');
    axios
      .get(`${BASE_URL}/api/lab/lab-tests`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        params: { hospital_id: hospitalId }
      })
      .then((res) => setLabTests(res.data || []))
      .catch(() => setLabTests([]));

    axios
      .get(`${BASE_URL}/api/lab/lab-test-combos`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        params: { hospital_id: hospitalId }
      })
      .then((res) => setCombos(res.data || []))
      .catch(() => setCombos([]));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const hospitalId = localStorage.getItem('hospitalId');
    const subHospitalId = localStorage.getItem('subHospitalId');
    const doctorId = localStorage.getItem('doctor_id');

    if (
      !appointment?.id ||
      !appointment?.patient_id ||
      !doctorId ||
      !selectedTest ||
      !urgency ||
      !preferredLab ||
      !hospitalId ||
      !subHospitalId
    ) {
      showWarning('All fields are required.');
      setSubmitting(false);
      return;
    }

    let testType = '';
    if (type === 'single') {
      const test = labTests.find((t) => t.id === Number(selectedTest));
      testType = test ? test.name : '';
    } else {
      const combo = combos.find((c) => c.id === Number(selectedTest));
      testType = combo ? combo.name : '';
    }

    try {
      await axios.post(
        `${BASE_URL}/api/lab/request`,
        {
          appointment_id: appointment?.id,
          patient_id: appointment?.patient_id,
          doctor_id: doctorId,
          test_type: testType,
          urgency,
          preferred_lab: preferredLab,
          hospital_id: hospitalId,
          sub_hospital_id: subHospitalId,
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        }
      );
      toast.success('Lab Test Requested and Lab Notified!');
      setSubmitting(false);
      onClose();
    } catch (err) {
      toast.error('Failed to request lab test.');
      setSubmitting(false);
    }
  };

  const handleReportFileChange = (labTestId, file) => {
    setReportFiles((prev) => ({ ...prev, [labTestId]: file }));
  };

  const handleCompleteLabTest = async (labTestId) => {
    const file = reportFiles[labTestId];
    if (!file) {
      toast.error('Please select a report file to upload.');
      return;
    }
    const formData = new FormData();
    formData.append('lab_test_id', labTestId);
    formData.append('report', file);

    try {
      await axios.post(`${BASE_URL}/api/lab/complete`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success('Lab test marked as complete and report uploaded!');
      setLabRequests(labRequests.filter((lr) => lr.id !== labTestId));
    } catch (err) {
      toast.error('Failed to complete lab test.');
    }
  };

  return (
    <div>
      {/* Doctor Side: Request Form */}
      <Form onSubmit={handleSubmit} className="mb-4">
        <Form.Group className="mb-3">
          <Form.Label className="fw-semibold">Type</Form.Label>
          <Form.Select
            value={type}
            onChange={(e) => {
              setType(e.target.value);
              setSelectedTest('');
            }}
          >
            <option value="single">Single</option>
            <option value="combo">Combo</option>
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="fw-semibold">
            {type === 'single' ? 'Select Lab Test' : 'Select Combo'}
          </Form.Label>
          <Form.Select
            value={selectedTest}
            onChange={(e) => setSelectedTest(e.target.value)}
            required
          >
            <option value="">Select...</option>
            {type === 'single'
              ? labTests.map((test) => (
                  <option key={test.id} value={test.id}>
                    {test.name} ({test.type})
                  </option>
                ))
              : combos.map((combo) => (
                  <option key={combo.id} value={combo.id}>
                    {combo.name} [{combo.tests.map((t) => t.name).join(', ')}]
                  </option>
                ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="fw-semibold">Urgency</Form.Label>
          <Form.Select
            value={urgency}
            onChange={(e) => setUrgency(e.target.value)}
          >
            <option value="Normal">Normal</option>
            <option value="Urgent">Urgent</option>
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="fw-semibold">Preferred Lab</Form.Label>
          <Form.Select
            value={preferredLab}
            onChange={(e) => setPreferredLab(e.target.value)}
            required
          >
            <option value="">Select Lab</option>
            {labs.map((lab) => (
              <option key={lab.id} value={lab.id}>
                {lab.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <div className="d-flex justify-content-end gap-2">
          <Button
            className="global-btn"
            type="button"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button className="global-btn" type="submit" disabled={submitting}>
            {submitting ? 'Requesting...' : 'Request Lab Test'}
          </Button>
        </div>
      </Form>

      {/* Lab Side: Requests Table */}
      {labRequests.length > 0 && (
        <div className="mt-4">
          <h6 className="fw-bold text-primary mb-3">Pending Lab Test Actions</h6>
          <Table striped bordered hover responsive size="sm">
            <thead>
              <tr>
                <th>Receipt #</th>
                <th>Patient Name</th>
                <th>Test Type</th>
                <th>Urgency</th>
                <th>Upload Report</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {labRequests.map((lr) => (
                <tr key={lr.id}>
                  <td>{lr.receipt_no || '-'}</td>
                  <td>{lr.patient_name || '-'}</td>
                  <td>{lr.test_type}</td>
                  <td>{lr.urgency}</td>
                  <td>
                    <Form.Control
                      type="file"
                      accept="application/pdf,image/*"
                      onChange={(e) =>
                        handleReportFileChange(lr.id, e.target.files[0])
                      }
                      size="sm"
                    />
                  </td>
                  <td>
                    <Button
                      size="sm"
                      variant="success"
                      onClick={() => handleCompleteLabTest(lr.id)}
                      disabled={!reportFiles[lr.id]}
                    >
                      Complete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default LabTest;
